import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import CardTitle from "@saleor/components/CardTitle";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { StaffErrorFragment } from "@saleor/fragments/types/StaffErrorFragment";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { getCityChoices, getProvinceChoices } from "@saleor/staff/utils";
import { getFormErrors } from "@saleor/utils/errors";
import getStaffErrorMessage from "@saleor/utils/errors/staff";
import getAccountErrorMessage from "@saleor/utils/errors/staff";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";

import { PROMO_CREATOR_CHOICES } from "./constants";

const useStyles = makeStyles(
  theme => ({
    prop: {
      marginBottom: theme.spacing(2)
    },
    propGrid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridRowGap: theme.spacing(1),
      gridTemplateColumns: "1fr 1fr",
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr"
      }
    },
    root: {
      display: "grid",
      gridColumnGap: theme.spacing(4),
      gridTemplateColumns: "1fr"
    }
  }),
  { name: "StaffCreateProperties" }
);

interface StaffCreatePropertiesProps {
  className?: string;
  data: {
    email: string;
    firstName: string;
    lastName: string;
    contactNo: string;
    password: string;
    companyName: string;

    permissionGroup: string;
    streetBrgy: string;
    city: string;
    promoCreator: string;
    provinceCode: string;
    provinceName: string;
    postalCode: string;
    country: string;
    mapPlaceId: string;
  };
  localError: {
    contactNo: string;
    firstName: string;
    lastName: string;
    companyName: string;
    streetBrgy: string;
    provinceCode: string;
    city: string;
    postalCode: string;
  };
  errors: StaffErrorFragment[];
  disabled: boolean;
  onChange: (event: React.ChangeEvent<any>) => void;
  selectedPermissionGroup: string;
  isRunnerRider: boolean;
  hasManageCouponsPermission: boolean;
}

const StaffCreateProperties: React.FC<StaffCreatePropertiesProps> = props => {
  const {
    className,
    data,
    localError,
    errors,
    onChange,
    selectedPermissionGroup,
    isRunnerRider,
    hasManageCouponsPermission
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();

  const [provinceDisplayValue, setProvinceDisplayValue] = useStateFromProps(
    maybe(() => data.provinceCode, "")
  );
  const [selectedProvince, setSelectedProvince] = React.useState("");
  const [selectedProvinceName, setSelectedProvinceName] = React.useState("");
  const [selectedCity, setSelectedCity] = React.useState("");

  const [provinceChoices, setProvinceChoices] = React.useState([]);
  const [cityChoices, setCityChoices] = React.useState([]);

  const formFields = [
    "firstName",
    "lastName",
    "email",
    "contactNo",
    "firstName",
    "password"
  ];
  const formErrors = getFormErrors(formFields, errors || []);

  useEffect(() => {
    setProvinceChoices(getProvinceChoices());
  }, [selectedPermissionGroup]);

  const onProvinceChange = createSingleAutocompleteSelectHandler(
    onChange,
    selectedValue => {
      setSelectedCity("");
      setCityChoices([]);

      setProvinceDisplayValue(selectedValue);
      setSelectedProvinceName(selectedValue);

      const provinceObject = provinceChoices.find(
        pc => pc.label === selectedValue
      );
      setSelectedProvince(provinceObject?.value);
    },
    provinceChoices
  );

  useEffect(() => {
    if (selectedProvince) {
      setCityChoices(getCityChoices(selectedProvince));
    }
  }, [selectedProvince]);

  const onCityChange = createSingleAutocompleteSelectHandler(
    onChange,
    selectedValue => {
      setSelectedCity(selectedValue);
    },
    cityChoices
  );

  useEffect(() => {
    data.provinceName = selectedProvinceName;
  }, [selectedProvinceName]);

  return (
    <Card className={className}>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "User Information",
          description: "section header"
        })}
      />
      <CardContent>
        <div className={classes.root}>
          <div>
            <div className={classes.propGrid}>
              <div className={classes.prop}>
                <TextField
                  id="firstNameTxt"
                  error={!!formErrors.firstName || !!localError.firstName}
                  label={intl.formatMessage(commonMessages.firstName)}
                  value={data.firstName}
                  name="firstName"
                  onChange={onChange}
                  helperText={
                    (!!localError.firstName && localError.firstName) ||
                    getAccountErrorMessage(formErrors.firstName, intl)
                  }
                  fullWidth
                />
              </div>
              <div className={classes.prop}>
                <TextField
                  id="lastNameTxt"
                  error={!!formErrors.lastName || !!localError.lastName}
                  label={intl.formatMessage(commonMessages.lastName)}
                  value={data.lastName}
                  name="lastName"
                  onChange={onChange}
                  helperText={
                    (!!localError.lastName && localError.lastName) ||
                    getAccountErrorMessage(formErrors.lastName, intl)
                  }
                  fullWidth
                />
              </div>

              {maybe(() => isRunnerRider) && (
                <div className={classes.prop}>
                  <TextField
                    id="companyNameTxt"
                    error={!!formErrors.companyName || !!localError.companyName}
                    label={intl.formatMessage(commonMessages.companyName)}
                    value={data.companyName}
                    name="companyName"
                    onChange={onChange}
                    helperText={
                      (!!localError.companyName && localError.companyName) ||
                      getAccountErrorMessage(formErrors.companyName, intl)
                    }
                    fullWidth
                  />
                </div>
              )}
              <div className={classes.prop}>
                <TextField
                  id="contactNumberTxt"
                  // error={!!formErrors.contactNo}
                  error={!!formErrors.contactNo || !!localError.contactNo}
                  // helperText={getAccountErrorMessage(
                  //   formErrors.contactNo,
                  //   intl
                  // )}
                  helperText={
                    (!!localError.contactNo && localError.contactNo) ||
                    getAccountErrorMessage(formErrors.contactNo, intl)
                  }
                  label={intl.formatMessage(commonMessages.contactNumber)}
                  value={data.contactNo}
                  name="contactNo"
                  onChange={onChange}
                  inputProps={{
                    maxLength: 12
                  }}
                  onKeyPress={event => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  fullWidth
                />
              </div>

              {maybe(() => hasManageCouponsPermission) && (
                <div className={classes.prop}>
                  <SingleSelectField
                    name="promoCreator"
                    choices={PROMO_CREATOR_CHOICES}
                    onChange={onChange}
                    label={intl.formatMessage({
                      defaultMessage: "Promo Creator"
                    })}
                    value={data.promoCreator}
                    lowerDropdown
                  />
                </div>
              )}

              {maybe(() => isRunnerRider) && (
                <>
                  <div className={classes.prop}>
                    <TextField
                      id="streetBrgyTxt"
                      error={!!formErrors.streetBrgy || !!localError.streetBrgy}
                      label={intl.formatMessage(commonMessages.homeAddressBrgy)}
                      value={data.streetBrgy}
                      name="streetBrgy"
                      onChange={onChange}
                      helperText={
                        (!!localError.streetBrgy && localError.streetBrgy) ||
                        getAccountErrorMessage(formErrors.streetBrgy, intl)
                      }
                      fullWidth
                    />
                  </div>
                  <div className={classes.prop}>
                    <SingleAutocompleteSelectField
                      displayValue={provinceDisplayValue}
                      error={
                        !!formErrors.provinceCode || !!localError.provinceCode
                      }
                      helperText={
                        (!!localError.provinceCode &&
                          localError.provinceCode) ||
                        getAccountErrorMessage(formErrors.provinceCode, intl)
                      }
                      label={intl.formatMessage(
                        commonMessages.homeAddressProvince
                      )}
                      name="provinceCode"
                      onChange={onProvinceChange}
                      value={selectedProvince}
                      choices={provinceChoices}
                      InputProps={{
                        autoComplete: "off"
                      }}
                      checkValueLabel={true}
                    />
                  </div>
                  <div className={classes.prop}>
                    <SingleAutocompleteSelectField
                      displayValue={selectedCity}
                      error={!!formErrors.city || !!localError.city}
                      helperText={
                        (!!localError.city && localError.city) ||
                        getAccountErrorMessage(formErrors.city, intl)
                      }
                      label={intl.formatMessage(commonMessages.homeAddressCity)}
                      name="city"
                      onChange={onCityChange}
                      value={selectedCity}
                      choices={cityChoices}
                      InputProps={{
                        autoComplete: "off"
                      }}
                      checkValueLabel={true}
                    />
                  </div>
                  <div className={classes.prop}>
                    <TextField
                      id="postalCodeTxt"
                      error={!!formErrors.postalCode || !!localError.postalCode}
                      label={intl.formatMessage(
                        commonMessages.homeAddressZipCode
                      )}
                      value={data.postalCode}
                      name="postalCode"
                      onChange={onChange}
                      helperText={
                        (!!localError.postalCode && localError.postalCode) ||
                        getStaffErrorMessage(formErrors.postalCode, intl)
                      }
                      fullWidth
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
StaffCreateProperties.displayName = "StaffCreateProperties";
export default StaffCreateProperties;
